import type { Rng } from '../rng.js';
import type { BuffTileSpec, PathSpec, Point } from '../scenario.js';

/**
 * Map generation (TRD-13 §4).
 *
 * Produces the paths and the build mask. Tile layers are NOT produced here — they are a
 * rendering concern and `@prism/content`'s autotiler resolves them, which also keeps
 * authored and generated maps going through one compiler (TRD-02 R-02-15).
 */

export const MAP_W = 32;
export const MAP_H = 18;

export interface GeneratedMap {
  paths: PathSpec[];
  core: Point;
  spawns: Point[];
  buildMask: string[];
  blockers: { x0: number; y0: number; x1: number; y1: number }[];
  buffTiles: BuffTileSpec[];
}

/** An authored buff-tile kind, weighted for the draw. Mechanics ride along verbatim. */
export interface BuffTileKind {
  id: string;
  weight: number;
  grantsClass?: string;
  rangeBonus?: number;
  onHitStatus?: { status: string; magnitude: number; durationTicks: number };
  coreHpEveryNWaves?: number;
}

export interface MapGenTuning {
  pathLength: readonly [number, number];
  terrainDensity: readonly [number, number];
  /** Authored kinds to draw from. Empty means the map gets no buff tiles. */
  buffTileKinds?: readonly BuffTileKind[];
  /** How many to place, inclusive. */
  buffTileCount?: readonly [number, number];
}

/** Buff tiles never sit within this of each other, or one aura covers the lot. */
const BUFF_TILE_SPACING = 5;

const MARGIN = 2;
const MIN_SEGMENT = 3;
const MAX_SEGMENT = 7;
/** Distance 2 permits a tight serpentine — a good tower spot — without smearing into a blob. */
const SELF_AVOID = 2;
/** Clearance between DIFFERENT lanes — they only need to read as separate. */
const PATH_SPACING = 1;
/** Truncated segments a walk may suffer before it gives up. */
const STUCK_TOLERANCE = 10;

/**
 * Rows the second lane retries from, after its first attempt fails.
 *
 * Spread across the lower half rather than adjacent, because a walk that could not fit
 * from row 14 usually cannot fit from row 13 either — the blockage is the first lane's
 * shape, and escaping it needs a different part of the board, not a different tile.
 *
 * Four, measured: doubling to eight rows moved the two-lane rate by 1.5 points, which is
 * where the start row stops being what is in the way.
 */
const SECOND_LANE_ROWS = [11, 15, 8, 13] as const;

/** The four orthogonal offsets, for adjacency checks. */
const NEIGHBOURS: readonly (readonly [number, number])[] = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

type Dir = { dx: number; dy: number };
const DIRS: Dir[] = [
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
];

function inBounds(x: number, y: number, margin = MARGIN): boolean {
  return x >= margin && y >= margin && x < MAP_W - margin && y < MAP_H - margin;
}

function key(x: number, y: number): number {
  return y * MAP_W + x;
}

/**
 * A segmented self-avoiding walk toward the Core (R-13-06, R-13-07).
 *
 * Direction blends wandering with a pull toward the Core that grows as the walk
 * approaches its length target, so it produces serpentines that still terminate — no
 * pathfinder, and no risk of a walk that circles forever.
 *
 * Returns null if it paints itself into a corner. The caller rerolls the whole map rather
 * than backtracking: backtracking makes the number of RNG draws depend on the data, which
 * is miserable to debug when a seed diverges (R-13-11).
 */
function walkPath(
  rng: Rng,
  start: Point,
  core: Point,
  targetLength: number,
  occupied: Set<number>,
  reserved: Set<number>,
): { waypoints: Point[]; length: number } | null {
  const waypoints: Point[] = [{ ...start }];

  /**
   * Tiles the walk must stay clear of.
   *
   * A tile only becomes collidable once the walk has moved TRAIL_LAG steps past it.
   * Without that lag the check rejects the walk's own tail — walking straight, the tile
   * two behind sits inside the 5×5 self-avoidance window and is already claimed, so every
   * walk died on its third step and the generator produced nothing at all.
   */
  const collidable = new Set<number>();
  const trail: number[] = [];
  const TRAIL_LAG = 5;

  let x = start.x;
  let y = start.y;
  let length = 0;
  let dir: Dir = DIRS[0] as Dir;
  let stuck = 0;

  const claim = (px: number, py: number): void => {
    trail.push(key(px, py));
    if (trail.length > TRAIL_LAG) {
      const aged = trail[trail.length - 1 - TRAIL_LAG];
      if (aged !== undefined) collidable.add(aged);
    }
  };
  claim(x, y);

  for (let segment = 0; segment < 64; segment++) {
    if (length >= targetLength) break;

    // Squared by multiplication — the ** operator has engine-dependent precision
    // (TRD-00 R-00-04), and a client and server disagreeing here generate different maps.
    const progress = Math.min(1, length / targetLength);
    const pull = progress * progress;
    const options = DIRS.filter((d) => !(d.dx === -dir.dx && d.dy === -dir.dy));

    const weights = options.map((d) => {
      const toCoreX = Math.sign(core.x - x);
      const toCoreY = Math.sign(core.y - y);
      const towardCore = (d.dx === toCoreX && d.dx !== 0) || (d.dy === toCoreY && d.dy !== 0);
      // Wander early, home late. Squaring `pull` keeps the walk meandering for most of
      // its length and then commits.
      return (towardCore ? 1 : 0.35) * pull + 0.5 * (1 - pull);
    });

    const chosen = rng.weightedPick(options, weights);
    const len = rng.nextInt(MIN_SEGMENT, MAX_SEGMENT + 1);

    // Trial-walk the segment before committing any of it.
    const trial: Point[] = [];
    let tx = x;
    let ty = y;
    for (let i = 0; i < len; i++) {
      tx += chosen.dx;
      ty += chosen.dy;
      if (!inBounds(tx, ty)) break;
      if (tooClose(tx, ty, collidable, occupied, reserved)) break;
      trial.push({ x: tx, y: ty });
    }

    // A blocked step truncates the segment rather than discarding it. Requiring the whole
    // segment to fit aborted ~89% of walks — most of them after making perfectly good
    // progress and then clipping a corner on the final tile.
    if (trial.length < 2) {
      if (++stuck >= STUCK_TOLERANCE) return null;
      continue;
    }

    stuck = 0;
    const end = trial[trial.length - 1] as Point;
    for (const p of trial) claim(p.x, p.y);
    x = end.x;
    y = end.y;
    length += trial.length;
    dir = chosen;
    waypoints.push({ x, y });
  }

  // Close onto the Core with an L, never a diagonal.
  //
  // This used to be `waypoints.push({ ...core })`, which is only correct when the walk
  // happens to stop on the Core's row or column — every other time it appended a raw
  // diagonal from wherever the walk ran out to wherever the Core was. Two things fell out
  // of that, and both were visible in a screenshot: enemies slid diagonally on the final
  // approach while moving on the grid everywhere else, and `rasterize` turned the diagonal
  // into a staircase that cut clean across whatever corridors lay between — so the Core
  // ended up sitting on a stretch of track that carried on past it, which reads as "the
  // goal spawned in the middle of the path".
  for (const point of connectToCore(x, y, core, rng, collidable, occupied)) {
    length += Math.abs(point.x - x) + Math.abs(point.y - y);
    x = point.x;
    y = point.y;
    waypoints.push(point);
  }

  return { waypoints, length };
}

/**
 * The two axis-aligned legs from the walk's end to the Core: across, then down, or the
 * other way round.
 *
 * Whichever order crosses fewer tiles that are already track wins, because a connector
 * that runs along an existing corridor is what makes the Core look like a waypoint rather
 * than a destination. The seeded coin-flip only decides which order is TRIED first, so it
 * breaks ties without making the choice arbitrary.
 *
 * Returns the corner and the Core, with zero-length steps dropped — a repeated waypoint
 * is a segment of length zero, and `compilePath` cannot take a direction from one.
 */
function connectToCore(
  x: number,
  y: number,
  core: Point,
  rng: Rng,
  collidable: Set<number>,
  occupied: Set<number>,
): Point[] {
  const preferXFirst = rng.chance(0.5);

  const legFor = (xFirst: boolean): Point[] => {
    const corner: Point = xFirst ? { x: core.x, y } : { x, y: core.y };
    const points: Point[] = [];
    if (corner.x !== x || corner.y !== y) points.push(corner);
    if (core.x !== corner.x || core.y !== corner.y) points.push({ ...core });
    return points;
  };

  const crossings = (points: readonly Point[]): number => {
    let count = 0;
    let cx = x;
    let cy = y;
    for (const point of points) {
      const stepX = Math.sign(point.x - cx);
      const stepY = Math.sign(point.y - cy);
      while (cx !== point.x || cy !== point.y) {
        cx += stepX;
        cy += stepY;
        // The Core tile itself is the destination; every path is meant to end on it.
        if (cx === core.x && cy === core.y) continue;
        const k = key(cx, cy);
        if (collidable.has(k) || occupied.has(k)) count++;
      }
    }
    return count;
  };

  const first = legFor(preferXFirst);
  if (crossings(first) === 0) return first;
  const second = legFor(!preferXFirst);
  return crossings(second) < crossings(first) ? second : first;
}

/**
 * Separation from earlier parts of this walk, from any previously generated path, and
 * from the tiles reserved around the Core.
 *
 * `reserved` is checked WITHOUT the 5×5 window: it is already an exclusion zone, and
 * widening it by another two tiles in every direction walls the Core off entirely.
 */
function tooClose(
  x: number,
  y: number,
  collidable: Set<number>,
  occupied: Set<number>,
  reserved: Set<number>,
): boolean {
  if (reserved.has(key(x, y))) return true;

  // Own trail: the wide window, which is what stops a walk smearing into a blob.
  for (let dy = -SELF_AVOID; dy <= SELF_AVOID; dy++) {
    for (let dx = -SELF_AVOID; dx <= SELF_AVOID; dx++) {
      if (collidable.has(key(x + dx, y + dy))) return true;
    }
  }

  // Another lane: a much tighter one. Held at SELF_AVOID as well, a 5x5 exclusion around
  // every tile of a 50-tile serpentine covers most of a 32x18 board, and the second walk
  // simply had nowhere to go — which is the other half of why two-lane maps never
  // appeared. Lanes only need to stay legibly apart, not respect a blob radius.
  for (let dy = -PATH_SPACING; dy <= PATH_SPACING; dy++) {
    for (let dx = -PATH_SPACING; dx <= PATH_SPACING; dx++) {
      if (occupied.has(key(x + dx, y + dy))) return true;
    }
  }
  return false;
}

/** Every tile a polyline passes through. */
export function rasterize(waypoints: readonly Point[]): Point[] {
  const out: Point[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    if (!a || !b) continue;
    const steps = Math.max(1, Math.abs(b.x - a.x) + Math.abs(b.y - a.y));
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const x = Math.round(a.x + (b.x - a.x) * t);
      const y = Math.round(a.y + (b.y - a.y) * t);
      const k = key(x, y);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ x, y });
    }
  }
  return out;
}

export function pathLengthOf(waypoints: readonly Point[]): number {
  let total = 0;
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    if (a && b) total += Math.abs(b.x - a.x) + Math.abs(b.y - a.y);
  }
  return total;
}

/**
 * Generate a candidate map. Returns null when the walk fails — the caller rerolls.
 */
export function generateMap(rng: Rng, tuning: MapGenTuning): GeneratedMap | null {
  // How many lanes to ATTEMPT (playtest request). One entry means one place worth
  // standing; two means a board has to answer both, which is the same pressure the buff
  // tiles apply from the other direction.
  //
  // An attempt, not a promise: about 45% of the rolls become a two-lane map, so a 0.55
  // roll lands near one day in four. Measured, not assumed.
  //
  // The gap USED to be far wider, and the reason recorded here was wrong. It blamed the
  // walk running out of board; in fact two thirds of every two-lane map that walked
  // perfectly well was then thrown away by V-G1, because the generator deliberately asked
  // for a shorter second lane and the validator refused anything under the single-lane
  // minimum. Two rules fighting each other, with the losing side invisible. Both were
  // fixed — the floor scales with lane count, and both lanes now give ground so they are
  // planned against the same board — and the remaining limit really is the walk.
  //
  // Raising this number now DOES move the rate, roughly proportionally, which it did not
  // before. It is a design choice about how special a two-lane day should be.
  const pathCount = rng.chance(0.55) ? 2 : 1;

  // Core sits on the right third, vertically central-ish, so paths have room to wander.
  const core: Point = { x: rng.nextInt(24, 29), y: rng.nextInt(6, 12) };
  const targetLength = rng.nextInt(tuning.pathLength[0], tuning.pathLength[1] + 1);

  const occupied = new Set<number>();
  const paths: PathSpec[] = [];
  const spawns: Point[] = [];

  // The Core has to be a DEAD END, not a tile the track happens to pass beside.
  //
  // Making the final leg an L already made the Core the true terminus of every path, but
  // a serpentine could still lay an unrelated leg right alongside it, and a goal with
  // track running past it reads as "the path continues" — which is the confusion this is
  // about. Keeping the WALK off the Core's doorstep leaves the connector as the only
  // thing that can reach it.
  //
  // Steering rather than rejecting, on measurement: over 150 days this leaves the Core
  // clean on every generated map AND raises generation acceptance from 87% to 91%, where
  // rejecting cluttered maps afterwards achieved the same cleanliness at a cost. Not
  // added to `occupied`, which is the set that gets painted as track.
  const reserved = new Set<number>();
  for (const [dx, dy] of NEIGHBOURS) reserved.add(key(core.x + dx, core.y + dy));

  for (let i = 0; i < pathCount; i++) {
    // Spawns on the left edge, separated vertically when there are two.
    const start: Point = { x: MARGIN, y: pathCount === 1 ? rng.nextInt(4, 14) : i === 0 ? 3 : 14 };
    // The second lane gets a SHORTER target. A 45-70 tile serpentine already fills most
    // of a 32x18 board, and the self-avoidance window is five tiles wide, so asking for
    // another of the same length is asking for something that does not fit.
    // Both lanes are planned against the SAME board, so on a two-lane map the first one
    // gives ground too. Leaving lane 0 at full length and only shrinking lane 1 is what
    // kept the second walk failing: the serpentine had already eaten the space before the
    // second walk started, so it succeeded on 15% of attempts. Shrinking both raises that
    // without making either lane trivial.
    const target =
      pathCount === 1
        ? targetLength
        : Math.round(targetLength * (i === 0 ? 0.8 : 0.6));
    // The second lane gets several attempts from different rows before being given up on.
    //
    // One attempt from one fixed row was the last thing holding two-lane days down: the
    // walk is greedy and self-avoiding, so whether it fits is mostly luck about where the
    // first serpentine happened to wander, and a single roll of that luck threw away maps
    // where a row two tiles up would have worked. Retrying is deterministic — every
    // attempt draws from the same seeded stream — so a given day still generates exactly
    // one map.
    let walk = walkPath(rng, start, core, target, occupied, reserved);
    if (!walk && i > 0) {
      for (const row of SECOND_LANE_ROWS) {
        walk = walkPath(rng, { x: MARGIN, y: row }, core, target, occupied, reserved);
        if (walk) {
          start.y = row;
          break;
        }
      }
    }

    // Only the FIRST lane is load-bearing. A map with no path is not a game, but a
    // second walk that cannot find room is just a one-lane day — and discarding the
    // whole map for it is why two-lane maps were not rare but IMPOSSIBLE: 0 of 115
    // generated days had one, at a nominal 55% chance.
    if (!walk) {
      if (i === 0) return null;
      break;
    }

    for (const tile of rasterize(walk.waypoints)) occupied.add(key(tile.x, tile.y));
    paths.push({ id: i, waypoints: walk.waypoints });
    spawns.push(start);
  }

  // ── Build mask ──
  const mask: string[][] = Array.from({ length: MAP_H }, () =>
    Array.from({ length: MAP_W }, () => '.'),
  );
  const set = (x: number, y: number, c: string): void => {
    const row = mask[y];
    if (row && x >= 0 && x < MAP_W) row[x] = c;
  };

  // ── Terrain (R-13-13) ──
  //
  // Blobs exist to create placement asymmetry. A map where every path tile has equally
  // good build space is a map where placement does not matter (R-13-15). Each blob is
  // checked against the coverage floor BEFORE it is committed — repairing afterwards is
  // far more code than pre-checking (R-13-14).
  const blockers: { x0: number; y0: number; x1: number; y1: number }[] = [];
  const density =
    tuning.terrainDensity[0] +
    rng.nextFloat() * (tuning.terrainDensity[1] - tuning.terrainDensity[0]);
  const blobCount = Math.round(MAP_W * MAP_H * density * 0.08);

  for (let b = 0; b < blobCount; b++) {
    const w = rng.nextInt(2, 5);
    const h = rng.nextInt(2, 4);
    const x0 = rng.nextInt(0, MAP_W - w);
    const y0 = rng.nextInt(0, MAP_H - h);

    let clear = true;
    for (let y = y0; y < y0 + h && clear; y++) {
      for (let x = x0; x < x0 + w; x++) {
        if (occupied.has(key(x, y))) {
          clear = false;
          break;
        }
      }
    }
    if (!clear) continue;

    blockers.push({ x0, y0, x1: x0 + w - 1, y1: y0 + h - 1 });
    for (let y = y0; y < y0 + h; y++) for (let x = x0; x < x0 + w; x++) set(x, y, '#');
  }

  for (const k of occupied) set(k % MAP_W, Math.floor(k / MAP_W), 'p');
  for (const s of spawns) set(s.x, s.y, 's');
  set(core.x, core.y, 'c');

  const buffTiles = placeBuffTiles(rng, tuning, mask, occupied);

  return {
    paths,
    core,
    spawns,
    buildMask: mask.map((row) => row.join('')),
    blockers,
    buffTiles,
  };
}

/**
 * Scatter the buff tiles across buildable ground.
 *
 * Two rules carry the whole design, and both are about WHERE rather than how many.
 *
 * They are spaced, so no single support aura can cover them all — otherwise the clump
 * simply relocates onto the tiles and nothing is gained.
 *
 * And they are drawn across a deliberate SPREAD of firing quality, best to worst: with
 * ten towers and at most four tiles a player takes every one, so the decision cannot be
 * "which tiles do I contest". It has to be "is this buff worth a tower that only watches
 * one lane" — which exists only when some tiles sit in poor positions. Sorting candidates
 * by path coverage and then sampling across the range is what produces that.
 */
function placeBuffTiles(
  rng: Rng,
  tuning: MapGenTuning,
  mask: string[][],
  occupied: Set<number>,
): BuffTileSpec[] {
  const kinds = tuning.buffTileKinds ?? [];
  const range = tuning.buffTileCount ?? [1, 4];
  if (kinds.length === 0) return [];

  const wanted = rng.nextInt(range[0] as number, (range[1] as number) + 1);
  if (wanted <= 0) return [];

  // Every buildable tile, scored by how much path it watches — the same measure the
  // player is judging when they pick a spot.
  const path: Point[] = [];
  for (let y = 0; y < MAP_H; y++) {
    for (let x = 0; x < MAP_W; x++) {
      if (occupied.has(key(x, y))) path.push({ x, y });
    }
  }

  const candidates: { x: number; y: number; cover: number }[] = [];
  for (let y = 0; y < MAP_H; y++) {
    for (let x = 0; x < MAP_W; x++) {
      if (mask[y]?.[x] !== '.') continue;
      let cover = 0;
      for (const p of path) {
        const dx = p.x - x;
        const dy = p.y - y;
        if (dx * dx + dy * dy <= 16) cover++;
      }
      if (cover > 0) candidates.push({ x, y, cover });
    }
  }
  if (candidates.length === 0) return [];

  // Best coverage first, then walk the list in even strides so the chosen tiles span
  // prime real estate through to genuinely awkward ground.
  candidates.sort((a, b) => b.cover - a.cover || a.y - b.y || a.x - b.x);

  const chosen: BuffTileSpec[] = [];
  const usedKinds = new Set<string>();
  const stride = Math.max(1, Math.floor(candidates.length / wanted));
  for (let slot = 0; slot < wanted; slot++) {
    // Search forward from the stride position for the first tile far enough from the
    // ones already placed, rather than giving up on the slot.
    for (let probe = 0; probe < candidates.length; probe++) {
      const candidate = candidates[(slot * stride + probe) % candidates.length];
      if (!candidate) continue;
      const clear = chosen.every(
        (t) =>
          Math.abs(t.x - candidate.x) + Math.abs(t.y - candidate.y) >= BUFF_TILE_SPACING,
      );
      if (!clear) continue;

      // No repeats while the pool lasts — two Vantage Points on one map is one choice
      // wearing two hats, and the whole point is that each tile asks something different.
      const pool = (kinds as BuffTileKind[]).filter((k) => !usedKinds.has(k.id));
      const drawFrom = pool.length > 0 ? pool : (kinds as BuffTileKind[]);
      const kind = rng.weightedPick(
        drawFrom,
        drawFrom.map((k) => k.weight),
      );
      usedKinds.add(kind.id);
      chosen.push({
        x: candidate.x,
        y: candidate.y,
        kind: kind.id,
        ...(kind.grantsClass !== undefined ? { grantsClass: kind.grantsClass as never } : {}),
        ...(kind.rangeBonus !== undefined ? { rangeBonus: kind.rangeBonus } : {}),
        ...(kind.onHitStatus !== undefined
          ? { onHitStatus: kind.onHitStatus as BuffTileSpec['onHitStatus'] }
          : {}),
        ...(kind.coreHpEveryNWaves !== undefined
          ? { coreHpEveryNWaves: kind.coreHpEveryNWaves }
          : {}),
      });
      break;
    }
  }

  // The mask is deliberately NOT marked. `canPlace` requires '.', and the coverage gate
  // in gen/index counts '.' as buildable, so introducing a glyph here would make every
  // buff tile unbuildable — the exact opposite of the point. The tiles are their own list
  // on the config, which is what the renderer and the sim both read.
  return chosen;
}
